import type { Bundle, Practitioner, Reference } from '@medplum/fhirtypes'
import type { SeenMedplumClient } from '@seen/core/client'
import {
  dateStringSchema,
  type DateString,
  formatDateNumber,
} from '@seen/core/dates'
import { invariant } from '@seen/core/invariant'
import { CommentCommunication } from '@seen/core/models/communications/CommentCommunication'
import {
  type OosaFlagDetails,
  PatientFlag,
} from '@seen/core/models/flags/patientFlag'
import * as patients from '@seen/core/models/patients'
import type { Participant } from '@seen/core/models/patients/participant'
import { StandardTask } from '@seen/core/models/tasks/standard/standardTask'
import { isOfType } from '@seen/core/utils'
import { Button, cn, Tabs, TabsContent, TabsList, TabsTrigger } from '@seen/ui'
import { IconAlertCircle } from '@tabler/icons-react'
import type { JSX } from 'react'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'
import { z } from 'zod'
import { ActivityList } from '#src/components/activity/ActivityList.tsx'
import { ComboboxButton } from '#src/components/ComboboxButton.tsx'
import { Checkbox } from '#src/components/core/Checkbox.tsx'
import { Dialog } from '#src/components/core/dialog/Dialog.tsx'
import { DialogContent } from '#src/components/core/dialog/DialogContent.tsx'
import { DialogFooter } from '#src/components/core/dialog/DialogFooter.tsx'
import { DialogHeader } from '#src/components/core/dialog/DialogHeader.tsx'
import { DialogTitle } from '#src/components/core/dialog/DialogTitle.tsx'
import { DialogDisplay } from '#src/components/core/dialog/display/DialogDisplay.tsx'
import { Form } from '#src/components/core/form/Form.tsx'
import { FormErrorMessage } from '#src/components/core/form/FormErrorMessage.tsx'
import { FormField } from '#src/components/core/form/FormField.tsx'
import { FormLabel } from '#src/components/core/form/FormLabel.tsx'
import { Input } from '#src/components/core/input/Input.tsx'
import { ScrollArea } from '#src/components/core/scroll-area/ScrollArea.tsx'
import { Textarea } from '#src/components/core/Textarea.tsx'
import { DatePicker } from '#src/components/input/DatePicker.tsx'
import {
  isOosaIdtApprovalRequired,
  mergeOosaDetails,
  oosaIdtApprovalTaskTitle,
  oosaInvoluntaryDisenrollmentTaskTitle,
  oosaTimelineEventMessage,
} from '#src/components/oosa/oosaFlag.ts'
import {
  OosaIdtApprovalToast,
  oosaIdtApprovalToastOptions,
} from '#src/components/oosa/OosaIdtApprovalToast.tsx'
import { useOosaFlagActivity } from '#src/components/oosa/UseOosaFlagActivity.tsx'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useTransactionMutation } from '#src/hooks/useFhirBundleMutation.ts'
import { useForm } from '#src/hooks/useForm.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'

const oosaFlagFormSchema = z
  .object({
    startDate: z.string(),
    returnDate: z.string(),
    returnDateUnknown: z.boolean(),
    reason: z.string(),
    otherReason: z.string(),
    notes: z.string(),
  })
  .superRefine((data, ctx) => {
    if (!dateStringSchema.safeParse(data.startDate).success) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Start date is required',
        path: ['startDate'],
      })
    }
    if (
      !data.returnDateUnknown &&
      !dateStringSchema.safeParse(data.returnDate).success
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Return date is required',
        path: ['returnDate'],
      })
    }
    if (
      !data.returnDateUnknown &&
      dateStringSchema.safeParse(data.startDate).success &&
      dateStringSchema.safeParse(data.returnDate).success &&
      data.returnDate < data.startDate
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Return date must be on or after the start date',
        path: ['returnDate'],
      })
    }
    if (
      !(patients.OOSA_REASON_VALUES as readonly string[]).includes(data.reason)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Reason is required',
        path: ['reason'],
      })
    }
    if (
      data.reason === patients.OOSA_REASON.OTHER &&
      data.otherReason.trim() === ''
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Specify "Other" is required',
        path: ['otherReason'],
      })
    }
  })

interface OosaFlagFormValues {
  startDate: string
  returnDate: string
  returnDateUnknown: boolean
  reason: string
  otherReason: string
  notes: string
}

const REASON_OPTIONS = patients.OOSA_REASON_VALUES.map((value) => ({
  value,
  label: patients.OOSA_REASON_LABELS[value],
}))

const EMPTY_VALUES: OosaFlagFormValues = {
  startDate: '',
  returnDate: '',
  returnDateUnknown: false,
  reason: '',
  otherReason: '',
  notes: '',
}

export const OOSA_HISTORY_DIALOG_HEIGHT_PX = 530

type OosaDialogTab = 'update' | 'history'
type OosaDialogMode = 'create' | 'edit'

export interface OosaFlagDialogProps {
  participant: Participant
  author: Reference<Practitioner>
  open: boolean
  onOpenChange: (open: boolean) => void
  mode?: OosaDialogMode
  existingFlag?: PatientFlag
  flagsToReplace?: readonly PatientFlag[]
  historyFlagIds?: readonly string[]
  historyFlags?: readonly PatientFlag[]
  initialDetails?: OosaFlagDetails
  onSaved?: (details: OosaFlagDetails) => void
  onPersistedFlag?: (flag: PatientFlag) => void
  onSaveFailed?: () => void
  defaultTab?: OosaDialogTab
}

interface FlagPeriod {
  start: DateString
  end?: DateString
}

export function OosaFlagDialog({
  participant,
  author,
  open,
  onOpenChange,
  mode = 'create',
  existingFlag,
  flagsToReplace,
  historyFlagIds,
  historyFlags,
  initialDetails,
  onSaved,
  onPersistedFlag,
  onSaveFailed,
  defaultTab = 'update',
}: OosaFlagDialogProps): JSX.Element {
  const isReadOnly = useIsReadOnly()
  const navigate = useNavigate()
  const currentPractitioner = useCurrentPractitioner()
  const transactionMutation = useTransactionMutation()
  const form = useForm({
    schema: oosaFlagFormSchema,
    defaultValues: formValues(mode, existingFlag, initialDetails),
  })
  const startDate = form.watch('startDate')
  const returnDate = form.watch('returnDate')
  const returnDateUnknown = form.watch('returnDateUnknown')
  const needsIdtApproval =
    !returnDateUnknown && isOosaIdtApprovalRequired(startDate, returnDate)
  const previousNeedsIdtApproval = isOosaIdtApprovalRequired(
    existingFlag?.oosaDetails?.startDate ?? '',
    existingFlag?.oosaDetails?.returnDate ?? ''
  )
  const previousReturnDateUnknown = Boolean(
    existingFlag?.oosaDetails?.returnDateUnknown
  )
  const [tab, setTab] = useState<OosaDialogTab>(defaultTab)
  const existingFlagId = existingFlag?.toFhirResource().id
  const fallbackFlags = [
    ...(historyFlags ?? []),
    ...(existingFlag ? [existingFlag] : []),
  ]
  const activityFlagIds = [
    ...new Set([
      ...(historyFlagIds ?? []),
      ...fallbackFlags.flatMap((flag) => {
        const id = flag.toFhirResource().id
        return id != null ? [id] : []
      }),
      ...(existingFlagId ? [existingFlagId] : []),
    ]),
  ]
  const activity = useOosaFlagActivity(
    open ? activityFlagIds : undefined,
    open ? fallbackFlags : []
  )

  const resetSourceRef = useRef({
    existingFlag,
    initialDetails,
    defaultTab,
  })
  resetSourceRef.current = { existingFlag, initialDetails, defaultTab }

  useEffect(() => {
    const {
      existingFlag: flag,
      initialDetails: details,
      defaultTab: tabOnOpen,
    } = resetSourceRef.current
    if (!open || mode === 'create') {
      form.reset(EMPTY_VALUES)
      if (open) {
        setTab(tabOnOpen)
      }
      return
    }
    form.reset(formValues(mode, flag, details))
    setTab(tabOnOpen)
  }, [open, mode, form])

  const handleSubmit = form.handleSubmit(async (values) => {
    const reason = values.reason as patients.OosaReason
    const otherReason =
      reason === patients.OOSA_REASON.OTHER ? values.otherReason.trim() : ''
    const details: OosaFlagDetails = {
      startDate: dateStringSchema.parse(values.startDate),
      ...(values.returnDateUnknown
        ? { returnDateUnknown: true }
        : { returnDate: dateStringSchema.parse(values.returnDate) }),
      reason,
      notes: values.notes.trim(),
    }
    if (otherReason) {
      details.otherReason = otherReason
    }
    const createIdtTask =
      !details.returnDateUnknown &&
      isOosaIdtApprovalRequired(details.startDate, details.returnDate ?? '') &&
      !previousNeedsIdtApproval
    const createInvoluntaryTask =
      Boolean(details.returnDateUnknown) && !previousReturnDateUnknown
    const idtTaskTitle = createInvoluntaryTask
      ? oosaInvoluntaryDisenrollmentTaskTitle(participant.name)
      : oosaIdtApprovalTaskTitle(participant.name)
    onSaved?.(details)
    try {
      const bundle = await transactionMutation.mutateAsync(
        async (builder, { medplum }) => {
          // Search Flag copies are often a version behind. Patching them
          // fails the versionId test and the modal never closes.
          if (mode === 'edit' && existingFlag) {
            const latestFlag = await readLatestPatientFlag(
              medplum,
              existingFlag
            )
            builder.patch(latestFlag, (flag) => {
              flag.applyOosaDetails(details)
              flag.status = 'active'
            })
          } else {
            for (const flag of flagsToReplace ?? []) {
              const latestFlag = await readLatestPatientFlag(medplum, flag)
              builder.patch(latestFlag, (inactive) => {
                inactive.status = 'inactive'
              })
            }
            const period: FlagPeriod = {
              start: details.startDate,
            }
            if (details.returnDate) {
              period.end = details.returnDate
            }
            builder.create(
              PatientFlag.build({
                patient: participant.reference,
                author,
                flagCode: patients.ParticipantFlagCode.OOSA,
                period,
                reason: patients.formatStoredOosaReason(
                  details.reason,
                  details.otherReason
                ),
                notes: details.notes,
              })
            )
          }
          builder.create(
            new CommentCommunication({
              partOf: [participant.reference],
              message: oosaTimelineEventMessage(
                details,
                mode === 'edit' ? 'modified' : 'set on'
              ),
              sender: currentPractitioner.reference,
            })
          )
          if (createIdtTask || createInvoluntaryTask) {
            builder.create(
              buildOosaIdtApprovalTask(
                currentPractitioner.reference,
                participant,
                idtTaskTitle
              )
            )
          }
        }
      )
      for (const flag of patientFlagsFromBundle(bundle)) {
        onPersistedFlag?.(flag)
      }
      if (createIdtTask || createInvoluntaryTask) {
        const createdTask = standardTaskFromBundle(bundle)
        const toastId = oosaIdtApprovalToastId(participant.id)
        toast.custom(
          (id) => (
            <OosaIdtApprovalToast
              title={idtTaskTitle}
              onViewTask={
                createdTask?.id
                  ? () => {
                      void navigate(
                        `${participant.relativeUrl}/tasks/${createdTask.id}`
                      )
                    }
                  : undefined
              }
              onDismiss={() => toast.dismiss(id)}
            />
          ),
          { id: toastId, ...oosaIdtApprovalToastOptions }
        )
      }
      onOpenChange(false)
    } catch {
      onSaveFailed?.()
    }
  })

  const formBody = (
    <Form onSubmit={handleSubmit} className="flex flex-col">
      <DialogContent className="gap-6 px-6 pt-6">
        <div className="flex flex-col gap-2.5">
          <div className="flex gap-6">
            <FormField
              control={form.control}
              name="startDate"
              render={({ field: { value, onChange, ...fieldProps } }) => (
                <div className="flex flex-1 flex-col gap-1">
                  <FormLabel className="text-sm font-semibold">
                    Start date
                  </FormLabel>
                  <DatePicker
                    {...fieldProps}
                    value={value || null}
                    onChange={(date) => onChange(date ?? '')}
                    allowUndefined
                    placeholder="MM/DD/YYYY"
                    dateFormatter={formatDateNumber}
                    iconPosition="end"
                    className="w-full justify-between font-normal"
                  />
                </div>
              )}
            />
            <FormField
              control={form.control}
              name="returnDate"
              render={({ field: { value, onChange, ...fieldProps } }) => (
                <div className="flex flex-1 flex-col gap-1">
                  <FormLabel className="text-sm font-semibold">
                    Return Date
                  </FormLabel>
                  <DatePicker
                    {...fieldProps}
                    value={value || null}
                    onChange={(date) => {
                      onChange(date ?? '')
                      if (date) {
                        form.setValue('returnDateUnknown', false)
                      }
                    }}
                    allowUndefined
                    placeholder="MM/DD/YYYY"
                    dateFormatter={formatDateNumber}
                    iconPosition="end"
                    className="w-full justify-between font-normal"
                  />
                </div>
              )}
            />
          </div>
          <FormField
            control={form.control}
            name="returnDateUnknown"
            render={({ field: { value, onChange, ...fieldProps } }) => (
              <div className="flex items-center gap-2">
                <Checkbox
                  {...fieldProps}
                  value={value}
                  onChange={(checked) => {
                    onChange(checked)
                    if (checked) {
                      form.setValue('returnDate', '')
                    }
                  }}
                />
                <FormLabel
                  htmlFor={fieldProps.id}
                  className="text-sm font-medium"
                >
                  Return date unknown
                </FormLabel>
              </div>
            )}
          />
          {returnDateUnknown ? (
            <OosaInvoluntaryDisenrollmentAlert />
          ) : (
            needsIdtApproval && <OosaIdtApprovalAlert />
          )}
        </div>
        <FormField
          control={form.control}
          name="reason"
          render={({ field: { value, onChange, ...fieldProps } }) => (
            <div className="flex flex-col gap-1">
              <FormLabel className="text-sm font-semibold">
                Reason for OOSA
              </FormLabel>
              <ComboboxButton
                options={REASON_OPTIONS}
                value={
                  (patients.OOSA_REASON_VALUES as readonly string[]).includes(
                    value
                  )
                    ? (value as patients.OosaReason)
                    : undefined
                }
                onChange={onChange}
                placeholder="Please select one..."
                className="w-full justify-between"
                {...fieldProps}
              />
            </div>
          )}
        />
        {form.watch('reason') === patients.OOSA_REASON.OTHER && (
          <FormField
            control={form.control}
            name="otherReason"
            render={({ field: { value, onChange, ...fieldProps } }) => (
              <div className="flex flex-col gap-1">
                <FormLabel className="text-sm font-semibold">
                  Specify "Other"
                </FormLabel>
                <Input
                  {...fieldProps}
                  value={value}
                  onChange={(event) => onChange(event.target.value)}
                />
                <FormErrorMessage />
              </div>
            )}
          />
        )}
        <FormField
          control={form.control}
          name="notes"
          render={({ field: { value, onChange, ...fieldProps } }) => (
            <div className="flex flex-col gap-1">
              <FormLabel className="text-sm font-semibold">Notes</FormLabel>
              <Textarea
                {...fieldProps}
                value={value}
                onChange={onChange}
                rows={4}
                className="min-h-24"
              />
            </div>
          )}
        />
      </DialogContent>
      <DialogFooter className="gap-4 p-6 pt-0 sm:gap-4">
        <Button
          type="button"
          variant="outline"
          onClick={() => onOpenChange(false)}
          disabled={transactionMutation.isPending}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          loading={transactionMutation.isPending}
          disabled={isReadOnly}
        >
          Confirm
        </Button>
      </DialogFooter>
    </Form>
  )

  const isHistoryTab = tab === 'history'
  const historyFrameStyle = isHistoryTab
    ? {
        height: OOSA_HISTORY_DIALOG_HEIGHT_PX,
        maxHeight: OOSA_HISTORY_DIALOG_HEIGHT_PX,
      }
    : undefined

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && transactionMutation.isPending) {
          return
        }
        onOpenChange(nextOpen)
      }}
    >
      <DialogDisplay
        className="h-auto max-w-[520px] auto-rows-auto"
        notScrollable
      >
        <div
          data-oosa-dialog-tab={tab}
          className={cn(
            'flex min-h-0 flex-col',
            isHistoryTab && 'overflow-hidden'
          )}
          style={historyFrameStyle}
        >
          <div
            data-fake-focus-trap="true"
            // oxlint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- catches Radix's auto-focus on dialog open so no visible field shows a focus ring; Tab moves on to the real first focusable child
            tabIndex={0}
            className="absolute top-[-9999px] left-[-9999px] h-0 w-0"
          />
          <Tabs
            value={tab}
            onValueChange={(value) => {
              if (value === 'update' || value === 'history') {
                setTab(value)
              }
            }}
            className={cn(
              'flex min-h-0 flex-col',
              isHistoryTab && 'h-full overflow-hidden'
            )}
          >
            <DialogHeader className="shrink-0 flex-col justify-start gap-4 pb-0">
              <DialogTitle className="text-xl font-semibold tracking-tight">
                OOSA (Out of Service Area)
              </DialogTitle>
              <TabsList variant="underline" className="w-full justify-start">
                <TabsTrigger value="update">Update</TabsTrigger>
                <TabsTrigger value="history">
                  History{activity.length > 0 && ` · ${activity.length}`}
                </TabsTrigger>
              </TabsList>
            </DialogHeader>
            <TabsContent
              value="update"
              className="min-h-0 flex-1 overflow-y-auto"
            >
              {formBody}
            </TabsContent>
            <TabsContent
              value="history"
              className="mt-0 min-h-0 flex-1 overflow-hidden"
            >
              <ScrollArea className="h-full" scrollRestorationId={null}>
                <DialogContent>
                  {activity.length > 0 ? (
                    <ActivityList>{activity}</ActivityList>
                  ) : (
                    <span className="text-sm text-muted-foreground">
                      No changes recorded yet.
                    </span>
                  )}
                </DialogContent>
              </ScrollArea>
            </TabsContent>
          </Tabs>
        </div>
      </DialogDisplay>
    </Dialog>
  )
}

function formValues(
  mode: OosaDialogMode,
  flag: PatientFlag | undefined,
  fallback?: OosaFlagDetails
): OosaFlagFormValues {
  if (mode === 'create') {
    return EMPTY_VALUES
  }
  const details = mergeOosaDetails(flag?.oosaDetails, fallback)
  if (!details) {
    return EMPTY_VALUES
  }
  return {
    startDate: details.startDate,
    returnDate: details.returnDate ?? '',
    returnDateUnknown: Boolean(details.returnDateUnknown),
    reason: details.reason,
    otherReason: details.otherReason ?? '',
    notes: details.notes,
  }
}

function patientFlagsFromBundle(bundle: Bundle | undefined): PatientFlag[] {
  return (bundle?.entry ?? []).flatMap((entry) => {
    const resource = entry.resource
    if (!resource || !PatientFlag.isInstance(resource)) {
      return []
    }
    return [PatientFlag.fromFhirResource(resource)]
  })
}

function oosaIdtApprovalToastId(participantId: string | undefined): string {
  return `oosa-idt-approval-${participantId ?? 'unknown'}`
}

async function readLatestPatientFlag(
  medplum: SeenMedplumClient,
  flag: PatientFlag
): Promise<PatientFlag> {
  const id = flag.toFhirResource().id
  if (!id) {
    return flag
  }
  return medplum.readResourceClass(PatientFlag, id)
}

function buildOosaIdtApprovalTask(
  owner: Reference<Practitioner>,
  participant: Participant,
  title: string
): StandardTask {
  invariant(
    isOfType(owner, 'Practitioner'),
    'OOSA IDT approval task owner must be a Practitioner'
  )
  // Owner is the assignee. Task.for (participant) is the PPT so the
  // participant Tasks tab can find this task.
  const task = new StandardTask({ requester: owner })
  task.title = title
  task.ownerPractitioner = owner
  task.requester = owner
  task.participant = participant.reference
  return task
}

function standardTaskFromBundle(
  bundle: Bundle | undefined
): StandardTask | undefined {
  for (const entry of bundle?.entry ?? []) {
    const resource = entry.resource
    if (!resource || !StandardTask.isInstance(resource)) {
      continue
    }
    return StandardTask.fromFhirResource(resource)
  }
  return undefined
}

function OosaIdtApprovalAlert(): JSX.Element {
  return (
    <OosaIdtAlert
      title="OOSA requests for 30 or more days require approval by IDT."
      body="A task will be created to obtain IDT approval."
    />
  )
}

function OosaInvoluntaryDisenrollmentAlert(): JSX.Element {
  return (
    <OosaIdtAlert
      title="OOSA requests with an unknown return date require an IDT decision around involuntary disenrollment."
      body="A task will be created to obtain an IDT decision."
    />
  )
}

function OosaIdtAlert({
  title,
  body,
}: {
  title: string
  body: string
}): JSX.Element {
  return (
    <output className="flex w-full items-start gap-1 rounded border border-yellow-700 bg-yellow-50 px-2 py-1.5 text-yellow-700">
      <IconAlertCircle className="size-4 shrink-0" />
      <div className="flex flex-col">
        <p className="text-xs leading-4 font-semibold">{title}</p>
        <p className="text-xs leading-4 font-normal">{body}</p>
      </div>
    </output>
  )
}
